import React, { useState } from "react";
import { useAuth, useTasks } from "../../hooks/useFirebase";
import { TasksService } from "../../firebase/databaseService";

const EmployeeDashboard = ({ setActivePage }) => {
  const { user } = useAuth();
  const { tasks } = useTasks();

  // ========== STATES ==========
  const [activeTab, setActiveTab] = useState("today");
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showWorkModal, setShowWorkModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");
  const [modalSuccess, setModalSuccess] = useState("");
  const [detailFilter, setDetailFilter] = useState("all");
  const [historyDateFilter, setHistoryDateFilter] = useState("all");

  // ========== WORK FORM ==========
  const [workForm, setWorkForm] = useState({
    callDate: "", decisionMaker: "", requirement: "",
    painPoint: "", employeeNotes: "", status: "in_progress",
  });

  // ========== MY TASKS ==========
  const myTasks = tasks.filter((t) => t.assignedTo === user?.uid);
  const today = new Date().toISOString().split("T")[0];

  // ========== TAB FILTERS ==========
  const todayTasks = myTasks.filter((t) => t.dueDate === today && t.status !== "completed");
  const pendingTasks = myTasks.filter((t) => t.status === "pending");
  const inProgressTasks = myTasks.filter((t) => t.status === "in_progress");
  const completedTasks = myTasks.filter((t) => t.status === "completed");
  const overdueTasks = myTasks.filter((t) => t.status !== "completed" && t.dueDate < today);

  // ========== HISTORY: ALL COMPLETED ITEMS ACROSS ALL TASKS ==========
  const getAllCompletedItems = () => {
    const history = [];
    myTasks.forEach((task) => {
      (task.assignedItems || []).forEach((item) => {
        if (item.status === "completed") {
          history.push({
            ...item,
            taskTitle: task.title,
            taskType: task.taskType,
            taskDueDate: task.dueDate,
            taskId: task.id,
          });
        }
      });
    });

    // Sort by completedAt date (newest first)
    return history.sort((a, b) => {
      const dateA = a.completedAt || a.updatedAt || "";
      const dateB = b.completedAt || b.updatedAt || "";
      return dateB.localeCompare(dateA);
    });
  };

  const completedHistory = getAllCompletedItems();

  // ========== DATE RANGE FILTER FOR HISTORY ==========
  const filterHistoryByDate = (items) => {
    if (historyDateFilter === "all") return items;

    const now = new Date();
    let startDate;

    if (historyDateFilter === "today") {
      startDate = today;
    } else if (historyDateFilter === "week") {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      startDate = weekAgo.toISOString().split("T")[0];
    } else if (historyDateFilter === "month") {
      const monthAgo = new Date(now);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      startDate = monthAgo.toISOString().split("T")[0];
    } else if (historyDateFilter === "3months") {
      const threeMonthsAgo = new Date(now);
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      startDate = threeMonthsAgo.toISOString().split("T")[0];
    }

    return items.filter((item) => {
      const completedDate = (item.completedAt || item.updatedAt || "").split("T")[0];
      return completedDate >= startDate;
    });
  };

  const filteredHistory = filterHistoryByDate(completedHistory);

  // ========== DAILY WORK SUMMARY ==========
  const getDailySummary = () => {
    const dailyMap = {};

    myTasks.forEach((task) => {
      (task.assignedItems || []).forEach((item) => {
        if (item.completedAt) {
          const date = item.completedAt.split("T")[0];
          if (!dailyMap[date]) {
            dailyMap[date] = { date, count: 0, items: [] };
          }
          dailyMap[date].count++;
          dailyMap[date].items.push({ ...item, taskTitle: task.title });
        }
      });
    });

    return Object.values(dailyMap).sort((a, b) => b.date.localeCompare(a.date));
  };

  const dailySummary = getDailySummary();

  // ========== WEEKLY STREAK ==========
  const getWeeklyStreak = () => {
    let streak = 0;
    const dates = [...new Set(dailySummary.map((d) => d.date))].sort().reverse();
    for (let i = 0; i < 7; i++) {
      const checkDate = new Date();
      checkDate.setDate(checkDate.getDate() - i);
      const dateStr = checkDate.toISOString().split("T")[0];
      if (dates.includes(dateStr)) {
        streak++;
      } else {
        break;
      }
    }
    return streak;
  };

  const weeklyStreak = getWeeklyStreak();

  // ========== OVERALL STATS ==========
  const totalItems = myTasks.reduce((s, t) => s + (t.totalItems || t.assignedItems?.length || 0), 0);
  const completedItems = myTasks.reduce((s, t) => s + (t.assignedItems || []).filter((i) => i.status === "completed").length, 0);
  const overallProgress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  // ========== TASKS TO DISPLAY BASED ON TAB ==========
  const getTasksToDisplay = () => {
    switch (activeTab) {
      case "today": return todayTasks;
      case "pending": return pendingTasks;
      case "in_progress": return inProgressTasks;
      case "completed": return completedTasks;
      case "overdue": return overdueTasks;
      case "all":
      default: return myTasks;
    }
  };

  const displayTasks = getTasksToDisplay();

  // ========== HANDLERS ==========
  const openWorkModal = (task, item) => {
    setSelectedTask(task);
    setSelectedItem(item);
    setWorkForm({
      callDate: item.callDate || "", decisionMaker: item.decisionMaker || "",
      requirement: item.requirement || "", painPoint: item.painPoint || "",
      employeeNotes: item.employeeNotes || "", status: item.status || "in_progress",
    });
    setModalError(""); setModalSuccess("");
    setShowWorkModal(true);
  };

  const handleChange = (e) => {
    setWorkForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSaveWork = async () => {
    if (!selectedTask || !selectedItem) return;
    setModalLoading(true);
    setModalError("");
    try {
      const updates = {
        callDate: workForm.callDate, decisionMaker: workForm.decisionMaker,
        requirement: workForm.requirement, painPoint: workForm.painPoint,
        employeeNotes: workForm.employeeNotes, status: workForm.status,
        updatedBy: user?.name || user?.email || "Employee",
        updatedAt: new Date().toISOString(),
      };
      if (workForm.status === "completed") {
        updates.completedAt = new Date().toISOString();
      }
      await TasksService.updateTaskItem(selectedTask.id, selectedItem.id, updates);
      setModalSuccess("✅ Data saved! Admin ko update dikhega.");
      setTimeout(() => { setShowWorkModal(false); setModalSuccess(""); }, 1500);
    } catch (error) {
      setModalError("Failed: " + error.message);
    } finally {
      setModalLoading(false);
    }
  };

  const handleQuickStatus = async (task, itemId, status) => {
    try {
      const updates = {
        status, updatedBy: user?.name || "Employee", updatedAt: new Date().toISOString(),
      };
      if (status === "completed") updates.completedAt = new Date().toISOString();
      await TasksService.updateTaskItem(task.id, itemId, updates);
    } catch (error) {
      console.error("Quick update error:", error);
    }
  };

  const openTaskDetail = (task) => {
    setSelectedTask(task);
    setDetailFilter("all");
    setShowHistoryModal(true);
  };

  const getItemProgress = (task) => {
    const items = task.assignedItems || [];
    const done = items.filter((i) => i.status === "completed").length;
    const total = items.length;
    return { done, total, progress: total > 0 ? Math.round((done / total) * 100) : 0 };
  };

  const detailItems = selectedTask
    ? (selectedTask.assignedItems || []).filter((i) => detailFilter === "all" || i.status === detailFilter)
    : [];

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <div style={{ fontFamily: "Inter, sans-serif" }}>

      {/* ========== WELCOME HEADER ========== */}
      <div style={{
        background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
        borderRadius: 12, padding: "24px 28px", color: "white", marginBottom: 24,
        display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16,
      }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0 }}>
            👋 Welcome, {user?.name || "Employee"}!
          </h1>
          <p style={{ fontSize: 14, color: "#bfdbfe", margin: "4px 0 0" }}>
            📅 {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ backgroundColor: "rgba(255,255,255,0.15)", padding: "8px 20px", borderRadius: 8, textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: 800 }}>{overallProgress}%</div>
            <div style={{ fontSize: 11, color: "#bfdbfe" }}>Progress</div>
          </div>
          <div style={{ backgroundColor: "rgba(34,197,94,0.2)", padding: "8px 20px", borderRadius: 8, textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#4ade80" }}>{weeklyStreak}</div>
            <div style={{ fontSize: 11, color: "#bfdbfe" }}>Day Streak 🔥</div>
          </div>
        </div>
      </div>

      {/* ========== STATS ROW ========== */}
      <div className="admin-grid admin-grid-5" style={{ marginBottom: 24 }}>
        {[
          { label: "My Tasks", value: myTasks.length, icon: "📋", cls: "qs-blue" },
          { label: "Today's Tasks", value: todayTasks.length, icon: "📅", cls: "qs-purple" },
          { label: "In Progress", value: inProgressTasks.length, icon: "🔄", cls: "qs-orange" },
          { label: "Completed", value: completedTasks.length, icon: "✅", cls: "qs-green" },
          { label: "Overdue", value: overdueTasks.length, icon: "⚠️", cls: "qs-red" },
        ].map((s, i) => (
          <div key={i} className={`quick-stat ${s.cls}`}>
            <div className="quick-stat-icon">{s.icon}</div>
            <div className="quick-stat-info">
              <div className="quick-stat-label">{s.label}</div>
              <div className="quick-stat-value">{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ========== OVERALL PROGRESS + HISTORY STATS ========== */}
      <div className="admin-grid admin-grid-2" style={{ marginBottom: 24 }}>
        {/* Overall Progress */}
        <div className="stat-card" style={{ borderLeft: "4px solid #2563eb" }}>
          <div className="stat-inner">
            <div className="stat-text" style={{ flex: 1 }}>
              <div className="stat-label">📊 Overall Work Progress</div>
              <div className="stat-value" style={{ fontSize: 28 }}>{completedItems} / {totalItems} items</div>
              <div style={{ marginTop: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span className="text-12 text-gray-500">Progress</span>
                  <span className="text-12 font-bold" style={{ color: overallProgress >= 80 ? "#16a34a" : "#3b82f6" }}>{overallProgress}%</span>
                </div>
                <div style={{ width: "100%", height: 10, backgroundColor: "#f3f4f6", borderRadius: 9999, overflow: "hidden" }}>
                  <div style={{
                    width: `${overallProgress}%`, height: "100%",
                    background: overallProgress >= 80 ? "linear-gradient(90deg, #22c55e, #16a34a)" : overallProgress >= 40 ? "linear-gradient(90deg, #3b82f6, #2563eb)" : "linear-gradient(90deg, #f97316, #ea580c)",
                    borderRadius: 9999, transition: "width 0.5s",
                  }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* History Stats */}
        <div className="stat-card" style={{ borderLeft: "4px solid #22c55e" }}>
          <div className="stat-inner">
            <div className="stat-text" style={{ flex: 1 }}>
              <div className="stat-label">📜 Work History</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 8 }}>
                {[
                  { label: "Total Done", value: completedHistory.length, color: "#16a34a" },
                  { label: "This Week", value: filterHistoryByDate(completedHistory).filter(i => {
                    const d = (i.completedAt || "").split("T")[0];
                    const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
                    return d >= weekAgo.toISOString().split("T")[0];
                  }).length, color: "#2563eb" },
                  { label: "Today", value: completedHistory.filter(i => (i.completedAt || "").split("T")[0] === today).length, color: "#9333ea" },
                ].map((s, i) => (
                  <div key={i} style={{ textAlign: "center", padding: 8, backgroundColor: `${s.color}10`, borderRadius: 8 }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 10, color: "#6b7280" }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== TABS ========== */}
      <div className="admin-tabs" style={{ marginBottom: 24 }}>
        {[
          { id: "today", label: "📅 Today", count: todayTasks.length },
          { id: "all", label: "📋 All Tasks", count: myTasks.length },
          { id: "pending", label: "⏳ Pending", count: pendingTasks.length },
          { id: "in_progress", label: "🔄 In Progress", count: inProgressTasks.length },
          { id: "completed", label: "✅ Completed", count: completedTasks.length },
          { id: "overdue", label: "⚠️ Overdue", count: overdueTasks.length },
          { id: "history", label: "📜 Work History", count: completedHistory.length },
        ].map((t) => (
          <button key={t.id} className={`tab-btn ${activeTab === t.id ? "active" : ""}`} onClick={() => setActiveTab(t.id)}>
            {t.label} <span className="tab-badge">{t.count}</span>
          </button>
        ))}
      </div>

      {/* ========== TASKS VIEW (NOT HISTORY TAB) ========== */}
      {activeTab !== "history" && (
        <>
          {displayTasks.length === 0 ? (
            <div style={{ backgroundColor: "white", borderRadius: 12, padding: 48, textAlign: "center", border: "1px solid #f3f4f6" }}>
              <p style={{ fontSize: 48, margin: 0 }}>📭</p>
              <p style={{ color: "#9ca3af", fontSize: 14 }}>No tasks in this category.</p>
            </div>
          ) : (
            <div className="task-list">
              {displayTasks.map((task) => {
                const isOverdue = task.status !== "completed" && task.dueDate < today;
                const isToday = task.dueDate === today;
                const prog = getItemProgress(task);

                return (
                  <div key={task.id} className={`task-card ${task.status === "completed" ? "task-completed" : isOverdue ? "task-overdue" : task.priority === "high" ? "task-high" : "task-medium"}`}>
                    <div className="task-card-inner" style={{ flexDirection: "column", gap: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
                        <div>
                          <h4 className="task-title">{task.title}</h4>
                          <div className="task-meta" style={{ marginTop: 6 }}>
                            <span>🏷 {task.taskType?.replace("_", " ")}</span>
                            <span className={isOverdue ? "overdue" : ""} style={isToday ? { color: "#2563eb", fontWeight: 600 } : {}}>
                              📅 {task.dueDate}{isOverdue ? " (Overdue)" : isToday ? " (Today)" : ""}
                            </span>
                            <span>📦 {prog.done}/{prog.total} done</span>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                          <span className={`status-badge badge-priority-${task.priority}`}>{task.priority}</span>
                          <span className={`status-badge badge-${task.status}`}>{task.status.replace("_", " ")}</span>
                        </div>
                      </div>

                      {/* Description */}
                      {task.description && (
                        <div style={{ width: "100%", padding: 10, backgroundColor: "#f0f9ff", borderRadius: 8, border: "1px solid #bfdbfe", fontSize: 12, color: "#1e40af" }}>
                          📋 {task.description}
                        </div>
                      )}

                      {/* Progress Bar */}
                      <div style={{ width: "100%" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span className="text-12 text-gray-500">Progress</span>
                          <span className="text-12 font-bold" style={{ color: prog.progress === 100 ? "#16a34a" : "#3b82f6" }}>{prog.progress}%</span>
                        </div>
                        <div style={{ width: "100%", height: 6, backgroundColor: "#f3f4f6", borderRadius: 9999, overflow: "hidden" }}>
                          <div style={{ width: `${prog.progress}%`, height: "100%", backgroundColor: prog.progress === 100 ? "#22c55e" : prog.progress >= 50 ? "#3b82f6" : "#f97316", borderRadius: 9999 }} />
                        </div>
                      </div>

                      {/* Quick Items Preview (First 5 items) */}
                      <div style={{ width: "100%" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <h5 style={{ fontSize: 12, fontWeight: 700, color: "#374151", margin: 0 }}>📦 Items</h5>
                          <button
                            onClick={() => openTaskDetail(task)}
                            style={{
                              padding: "4px 12px", borderRadius: 6, border: "1px solid #bfdbfe",
                              background: "#eff6ff", color: "#1d4ed8", fontSize: 11, fontWeight: 700, cursor: "pointer",
                            }}
                          >
                            View All ({prog.total})
                          </button>
                        </div>

                        <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
                          <table className="data-table" style={{ width: "100%", fontSize: 11 }}>
                            <thead>
                              <tr>
                                <th style={{ padding: "6px 8px" }}>#</th>
                                <th style={{ padding: "6px 8px" }}>Lead ID</th>
                                <th style={{ padding: "6px 8px" }}>Business</th>
                                <th style={{ padding: "6px 8px" }}>Contact</th>
                                <th style={{ padding: "6px 8px" }}>Status</th>
                                <th style={{ padding: "6px 8px" }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(task.assignedItems || []).slice(0, 5).map((item, idx) => (
                                <tr key={item.id || idx} style={{
                                  backgroundColor: item.status === "completed" ? "#f0fdf4" : item.status === "in_progress" ? "#fff7ed" : "transparent"
                                }}>
                                  <td style={{ padding: "6px 8px", color: "#9ca3af" }}>{idx + 1}</td>
                                  <td style={{ padding: "6px 8px", fontWeight: 700, color: "#2563eb" }}>{item.leadId || "—"}</td>
                                  <td style={{ padding: "6px 8px", fontWeight: 600 }}>{item.businessName || item.name || "—"}</td>
                                  <td style={{ padding: "6px 8px", fontWeight: 600 }}>{item.contactNumber || item.phone || "—"}</td>
                                  <td style={{ padding: "6px 8px" }}>
                                    <span className={`status-badge badge-${item.status}`} style={{ fontSize: 9 }}>{item.status}</span>
                                  </td>
                                  <td style={{ padding: "6px 8px" }}>
                                    <div style={{ display: "flex", gap: 4 }}>
                                      <button onClick={() => openWorkModal(task, item)} style={{ padding: "2px 8px", borderRadius: 4, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#1d4ed8", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>✏️</button>
                                      {item.status !== "completed" && (
                                        <button onClick={() => handleQuickStatus(task, item.id, "completed")} style={{ padding: "2px 8px", borderRadius: 4, border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#166534", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>✅</button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {prog.total > 5 && (
                            <div style={{ padding: "6px 12px", textAlign: "center", fontSize: 11, color: "#2563eb", cursor: "pointer", fontWeight: 600 }}
                              onClick={() => openTaskDetail(task)}>
                              ... and {prog.total - 5} more items (Click to view all)
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ========== 📜 WORK HISTORY TAB ========== */}
      {activeTab === "history" && (
        <div>
          {/* Date Filter */}
          <div className="admin-toolbar" style={{ marginBottom: 20 }}>
            <div className="toolbar-left">
              <span style={{ fontSize: 14, fontWeight: 700, color: "#374151" }}>📅 Filter by Date:</span>
              {[
                { id: "all", label: "All Time" },
                { id: "today", label: "Today" },
                { id: "week", label: "This Week" },
                { id: "month", label: "This Month" },
                { id: "3months", label: "Last 3 Months" },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setHistoryDateFilter(f.id)}
                  style={{
                    padding: "6px 16px", borderRadius: 9999, fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all 0.2s",
                    border: historyDateFilter === f.id ? "2px solid #2563eb" : "1px solid #e5e7eb",
                    backgroundColor: historyDateFilter === f.id ? "#eff6ff" : "white",
                    color: historyDateFilter === f.id ? "#2563eb" : "#6b7280",
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="toolbar-right">
              <span style={{ fontSize: 13, color: "#6b7280" }}>
                Showing <strong>{filteredHistory.length}</strong> completed items
              </span>
            </div>
          </div>

          {/* Daily Summary Cards */}
          {dailySummary.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 12 }}>📅 Daily Work Summary</h3>
              <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 8 }}>
                {dailySummary.slice(0, 14).map((day) => (
                  <div
                    key={day.date}
                    style={{
                      minWidth: 120, padding: 12, borderRadius: 10,
                      backgroundColor: day.date === today ? "#eff6ff" : "#f9fafb",
                      border: day.date === today ? "2px solid #3b82f6" : "1px solid #e5e7eb",
                      textAlign: "center", cursor: "pointer", flexShrink: 0,
                    }}
                    onClick={() => {
                      setActiveTab("all");
                    }}
                  >
                    <div style={{ fontSize: 24, fontWeight: 800, color: day.date === today ? "#2563eb" : "#111827" }}>
                      {day.count}
                    </div>
                    <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600 }}>
                      {new Date(day.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                    </div>
                    {day.date === today && (
                      <div style={{ fontSize: 10, color: "#2563eb", fontWeight: 700, marginTop: 2 }}>TODAY</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* History Table */}
          {filteredHistory.length === 0 ? (
            <div style={{ backgroundColor: "white", borderRadius: 12, padding: 48, textAlign: "center", border: "1px solid #f3f4f6" }}>
              <p style={{ fontSize: 48, margin: 0 }}>📜</p>
              <p style={{ color: "#9ca3af", fontSize: 14, marginTop: 8 }}>No work history found for this period.</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {filteredHistory.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: "14px 16px", backgroundColor: "#f0fdf4", borderRadius: 10,
                    border: "1px solid #bbf7d0", borderLeft: "4px solid #22c55e",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <div style={{ fontSize: 20 }}>✅</div>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                            {item.businessName || item.name}
                          </div>
                          <div style={{ fontSize: 11, color: "#6b7280", display: "flex", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
                            <span style={{ color: "#2563eb", fontWeight: 700, fontFamily: "monospace" }}>{item.leadId || "—"}</span>
                            <span>• {item.contactNumber || item.phone || "—"}</span>
                            <span>• 📁 {item.taskTitle || "—"}</span>
                          </div>
                        </div>
                      </div>

                      {(item.callDate || item.decisionMaker || item.requirement || item.painPoint || item.employeeNotes) && (
                        <div style={{
                          marginTop: 8, padding: 10, backgroundColor: "white", borderRadius: 8,
                          border: "1px solid #e5e7eb", display: "grid", gap: 6, fontSize: 12,
                        }}>
                          {item.callDate && (
                            <div><span style={{ color: "#9ca3af" }}>📞 Call Date: </span><strong>{item.callDate}</strong></div>
                          )}
                          {item.decisionMaker && (
                            <div><span style={{ color: "#9ca3af" }}>👤 Decision Maker: </span><strong>{item.decisionMaker}</strong></div>
                          )}
                          {item.requirement && (
                            <div><span style={{ color: "#9ca3af" }}>📋 Requirement: </span><strong>{item.requirement}</strong></div>
                          )}
                          {item.painPoint && (
                            <div><span style={{ color: "#9ca3af" }}>⚠️ Pain Point: </span><strong>{item.painPoint}</strong></div>
                          )}
                          {item.employeeNotes && (
                            <div><span style={{ color: "#9ca3af" }}>📝 Notes: </span><strong>{item.employeeNotes}</strong></div>
                          )}
                        </div>
                      )}
                    </div>

                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <span style={{ fontSize: 11, color: "#16a34a", fontWeight: 700 }}>✅ Completed</span>
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                        {item.completedAt
                          ? new Date(item.completedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
                          : "—"
                        }
                      </div>
                      {item.completedAt && (
                        <div style={{ fontSize: 10, color: "#d1d5db" }}>
                          {new Date(item.completedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ==========================================
          WORK MODAL (Employee Data Fill)
      ========================================== */}
      {showWorkModal && selectedTask && selectedItem && (
        <div className="modal-overlay" onClick={() => setShowWorkModal(false)}>
          <div className="modal-box modal-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>✏️ Fill Data</h3>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: "#6b7280" }}>
                  {selectedItem.businessName || selectedItem.name} • {selectedItem.leadId || ""}
                </p>
              </div>
              <button className="modal-close-btn" onClick={() => setShowWorkModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {modalError && <div style={{ marginBottom: 12, padding: 12, backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }}><p style={{ color: "#991b1b", fontSize: 14, margin: 0 }}>❌ {modalError}</p></div>}
              {modalSuccess && <div style={{ marginBottom: 12, padding: 12, backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8 }}><p style={{ color: "#166534", fontSize: 14, margin: 0 }}>{modalSuccess}</p></div>}

              <div style={{ padding: 12, backgroundColor: "#f0f9ff", border: "1px solid #bfdbfe", borderRadius: 8, marginBottom: 16 }}>
                <h4 style={{ margin: "0 0 8px", fontSize: 13, color: "#1e40af" }}>📋 Lead Information</h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 13 }}>
                  <div><span style={{ color: "#6b7280" }}>Lead ID: </span><strong style={{ color: "#2563eb" }}>{selectedItem.leadId || "—"}</strong></div>
                  <div><span style={{ color: "#6b7280" }}>Category: </span><strong>{selectedItem.category || "—"}</strong></div>
                  <div><span style={{ color: "#6b7280" }}>Business: </span><strong>{selectedItem.businessName || selectedItem.name || "—"}</strong></div>
                  <div><span style={{ color: "#6b7280" }}>Contact: </span><strong>{selectedItem.contactNumber || selectedItem.phone || "—"}</strong></div>
                </div>
              </div>

              <h4 style={{ fontSize: 14, fontWeight: 700, color: "#374151", marginBottom: 12 }}>📝 Your Work Data:</h4>

              <div className="admin-form-group" style={{ marginBottom: 14 }}>
                <label>📞 Call Date *</label>
                <input type="date" name="callDate" value={workForm.callDate} onChange={handleChange} disabled={modalLoading} />
              </div>
              <div className="admin-form-group" style={{ marginBottom: 14 }}>
                <label>👤 Decision Maker</label>
                <input type="text" name="decisionMaker" value={workForm.decisionMaker} onChange={handleChange} placeholder="e.g. Mr. Sharma - CEO" disabled={modalLoading} />
              </div>
              <div className="admin-form-group" style={{ marginBottom: 14 }}>
                <label>📋 Requirement</label>
                <textarea name="requirement" value={workForm.requirement} onChange={handleChange} placeholder="What do they need?" rows="2" disabled={modalLoading} />
              </div>
              <div className="admin-form-group" style={{ marginBottom: 14 }}>
                <label>⚠️ Pain Point</label>
                <textarea name="painPoint" value={workForm.painPoint} onChange={handleChange} placeholder="What's their main problem?" rows="2" disabled={modalLoading} />
              </div>
              <div className="admin-form-group" style={{ marginBottom: 14 }}>
                <label>📝 Notes</label>
                <textarea name="employeeNotes" value={workForm.employeeNotes} onChange={handleChange} placeholder="Discussion summary..." rows="2" disabled={modalLoading} />
              </div>
              <div className="admin-form-group">
                <label>Status</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {[{ v: "pending", l: "⏳ Pending", c: "#6b7280" }, { v: "in_progress", l: "🔄 In Progress", c: "#f97316" }, { v: "completed", l: "✅ Completed", c: "#22c55e" }].map((opt) => (
                    <button key={opt.v} onClick={() => setWorkForm((p) => ({ ...p, status: opt.v }))} disabled={modalLoading}
                      style={{
                        padding: "8px 16px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
                        border: workForm.status === opt.v ? `2px solid ${opt.c}` : "1px solid #e5e7eb",
                        backgroundColor: workForm.status === opt.v ? `${opt.c}15` : "white",
                        color: workForm.status === opt.v ? opt.c : "#6b7280",
                      }}
                    >{opt.l}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowWorkModal(false)} disabled={modalLoading}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveWork} disabled={modalLoading}>{modalLoading ? "⏳ Saving..." : "💾 Save Data"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ==========================================
          FULL TASK DETAIL MODAL
      ========================================== */}
      {showHistoryModal && selectedTask && (
        <div className="modal-overlay" onClick={() => setShowHistoryModal(false)} style={{ alignItems: "flex-start", paddingTop: 40, overflow: "auto" }}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 960, width: "95%", maxHeight: "90vh", overflowY: "auto", padding: 0 }}>
            {/* Header */}
            <div style={{ padding: "20px 24px", borderBottom: "1px solid #e5e7eb", background: "linear-gradient(135deg, #f0f9ff, #ffffff)", position: "sticky", top: 0, zIndex: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <span className={`status-badge badge-${selectedTask.status}`} style={{ fontSize: 11, padding: "3px 12px" }}>{selectedTask.status.replace("_", " ").toUpperCase()}</span>
                    <span className={`status-badge badge-priority-${selectedTask.priority}`} style={{ fontSize: 11, padding: "3px 12px" }}>{selectedTask.priority.toUpperCase()}</span>
                  </div>
                  <h2 style={{ fontSize: 18, fontWeight: 800, color: "#111827", margin: "0 0 4px" }}>{selectedTask.title}</h2>
                  <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>📅 Due: {selectedTask.dueDate} • 📦 {(selectedTask.assignedItems || []).length} items</p>
                </div>
                <button onClick={() => setShowHistoryModal(false)} style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid #e5e7eb", background: "white", cursor: "pointer", fontSize: 16, color: "#6b7280" }}>✕</button>
              </div>
            </div>

            {/* Filter Tabs */}
            <div style={{ padding: "12px 24px", borderBottom: "1px solid #f3f4f6", display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[
                { id: "all", label: `All (${(selectedTask.assignedItems || []).length})` },
                { id: "completed", label: `✅ Done (${(selectedTask.assignedItems || []).filter((i) => i.status === "completed").length})` },
                { id: "in_progress", label: `🔄 In Progress (${(selectedTask.assignedItems || []).filter((i) => i.status === "in_progress").length})` },
                { id: "pending", label: `⏳ Pending (${(selectedTask.assignedItems || []).filter((i) => i.status === "pending").length})` },
              ].map((f) => (
                <button key={f.id} onClick={() => setDetailFilter(f.id)}
                  style={{
                    padding: "5px 14px", borderRadius: 9999, fontSize: 11, fontWeight: 700, cursor: "pointer",
                    border: detailFilter === f.id ? "2px solid #2563eb" : "1px solid #e5e7eb",
                    backgroundColor: detailFilter === f.id ? "#eff6ff" : "white",
                    color: detailFilter === f.id ? "#2563eb" : "#6b7280",
                  }}
                >{f.label}</button>
              ))}
            </div>

            {/* Items */}
            <div style={{ padding: 16, maxHeight: 500, overflowY: "auto" }}>
              {detailItems.length === 0 ? (
                <div style={{ textAlign: "center", padding: 32, color: "#9ca3af" }}>
                  <p style={{ fontSize: 32, margin: 0 }}>📭</p>
                  <p style={{ fontSize: 13 }}>No items</p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {detailItems.map((item, idx) => {
                    const statusColor = item.status === "completed" ? "#22c55e" : item.status === "in_progress" ? "#f97316" : "#9ca3af";
                    const bgColor = item.status === "completed" ? "#f0fdf4" : item.status === "in_progress" ? "#fff7ed" : "#f9fafb";
                    return (
                      <div key={item.id || idx} style={{ padding: 12, backgroundColor: bgColor, borderRadius: 8, borderLeft: `4px solid ${statusColor}`, border: `1px solid ${statusColor}20`, borderLeftWidth: 4, borderLeftStyle: "solid", borderLeftColor: statusColor }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                              <span style={{ fontSize: 14 }}>{item.status === "completed" ? "✅" : item.status === "in_progress" ? "🔄" : "⏳"}</span>
                              <strong style={{ fontSize: 13, color: "#111827" }}>{item.businessName || item.name}</strong>
                              <span style={{ fontSize: 11, color: "#2563eb", fontFamily: "monospace", fontWeight: 700 }}>{item.leadId || ""}</span>
                              <span style={{ fontSize: 11, color: "#6b7280" }}>• {item.contactNumber || item.phone || ""}</span>
                            </div>
                            {(item.callDate || item.decisionMaker || item.requirement || item.employeeNotes) && (
                              <div style={{ fontSize: 11, color: "#374151", paddingLeft: 26 }}>
                                {item.callDate && <div>📞 {item.callDate}</div>}
                                {item.decisionMaker && <div>👤 {item.decisionMaker}</div>}
                                {item.requirement && <div>📋 {item.requirement.substring(0, 60)}</div>}
                                {item.painPoint && <div>⚠️ {item.painPoint.substring(0, 60)}</div>}
                              </div>
                            )}
                          </div>
                          <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                            <button onClick={() => { setShowHistoryModal(false); openWorkModal(selectedTask, item); }} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#1d4ed8", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>✏️ Edit</button>
                            {item.status !== "completed" && (
                              <button onClick={() => handleQuickStatus(selectedTask, item.id, "completed")} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#166534", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>✅</button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: "12px 24px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "flex-end", position: "sticky", bottom: 0, background: "white" }}>
              <button className="btn btn-outline" onClick={() => setShowHistoryModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDashboard;